<script lang="ts" setup>
import { computed } from 'vue'
import { v4 as uuid } from 'uuid'
import type { HomePageConfig } from '../../configs/types'
import ModuleCard from './ModuleCard.vue'
import { resources } from '@/config/brands'

const props = defineProps<{
  module: any
  config: HomePageConfig
  currentTagId: string
  cardClass?: string
  headerClass?: string
  gotoBook: (id: string, firstChapterId?: string) => void
  gotoTags: (id: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

// 定义样式变量供 CSS v-bind 使用
const tagActiveBg = computed(() => props.config.theme.tagActiveBg)
const tagActiveText = computed(() => props.config.theme.tagActiveText)
const tagInactiveBg = computed(() => props.config.theme.tagInactiveBg)
const tagInactiveText = computed(() => props.config.theme.tagInactiveText)
const tagsBgStyle = computed(() => props.config.theme.tagsBg ? `url(${props.config.theme.tagsBg})` : 'none')

function handleSeeAll() {
  props.gotoTags(props.currentTagId)
}
</script>

<template>
  <div class="tags-container p-4">
    <ModuleCard
      :title-text="module.showName"
      :action-text-value="config.texts.seeAll"
      :card-class="cardClass"
      :header-class="headerClass"
      @action="handleSeeAll"
    >
      <!-- 标签区域 - 添加背景图 -->
      <div class="mt-2 rounded-12px">
        <div class="flex flex-wrap">
          <div
            v-for="tag in getCurrentShowTags(module)" :key="tag.id"
            class="tag-button flex items-center justify-center rounded-full mt-2 text-12px whitespace-nowrap py-1 px-3 mr-2"
            :class="{ 'tag-active': currentTagId === tag.id }"
            @click="handleTagClick(tag.id)"
          >
            <img
              v-if="tag.isHot"
              :src="resources.tagsIcons.hotIcon"
              class="thumbnail_2 mr-1" style="width: 12px; height: 12px;"
            >
            {{ tag.tagName }}
          </div>
        </div>
      </div>

      <!-- 书籍横向滚动区域 -->
      <div class="books-scroll-container mt-4">
        <div class="books-scroll-wrapper">
          <div
            v-for="item in getCurrentTagResources(module)"
            :key="item.id"
            class="book-item flex-shrink-0"
            @click="gotoBook(item.id, item.firstChapter?.id)"
          >
            <BizBookCover
              :id="uuid()"
              :src="item.contentCoverUrl || ''"
              :extra="{
                module_name: module.showName,
                module_id: module.id,
                module_type: module.columnType,
                ...item,
              }"
              class="rounded-8px"
            />
            <p class="mt-5px text-12px line-clamp-2 font-medium text-n-text-1">
              {{ item.contentName }}
            </p>
          </div>
        </div>
      </div>
    </ModuleCard>
  </div>
</template>

<style lang="scss" scoped>
// 标签容器 - 背景图由配置动态注入
.tags-container {
  background-image: v-bind(tagsBgStyle);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

// 标签按钮样式
.tag-button {
  background-color: v-bind(tagInactiveBg);
  color: v-bind(tagInactiveText);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
  }
}

.tag-button.tag-active {
  background-color: v-bind(tagActiveBg);
  color: v-bind(tagActiveText);
}

// 书籍横向滚动容器
.books-scroll-container {
  overflow: hidden;
}

.books-scroll-wrapper {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Opera */
  }
}

// 书籍项
.book-item {
  width: 96px;
  cursor: pointer;

  p {
    width: 96px;
  }
}
</style>
